import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../../app/store/utils/baseQueryWithReauth';

export type ChannelType = 'SHOP' | 'CARD' | 'MOBILE';

export interface IChannelMeta {
  defaultHost?: string | null;
  templateName?: string;
  hostType?: 'system' | 'personal';
  hostValue?: string;
  [key: string]: unknown;
}

export interface IChannelApiDto {
  id: number;
  stockOwnerId: number;
  companyId: number;
  type: ChannelType;
  isActive: boolean;
  host: string | null;
  meta: IChannelMeta | null;
  createdAt: string;
  updatedAt: string;
}

export interface IUpdateChannelHostMetaDto {
  id: number;
  hostType: 'system' | 'personal';
  hostValue: string;
}

function deserializeChannel(raw: Record<string, unknown>): IChannelApiDto {
  return {
    id: Number(raw.id),
    stockOwnerId: Number(raw.stockOwnerId ?? raw.stock_owner_id ?? 0),
    companyId: Number(raw.companyId ?? raw.company_id ?? 0),
    type: raw.type as ChannelType,
    isActive: Boolean(raw.isActive ?? raw.is_active),
    host: typeof raw.host === 'string' ? raw.host : null,
    meta: raw.meta && typeof raw.meta === 'object' ? (raw.meta as IChannelMeta) : null,
    createdAt: String(raw.createdAt ?? raw.created_at ?? ''),
    updatedAt: String(raw.updatedAt ?? raw.updated_at ?? ''),
  };
}

/** System deploy host — always `channel.host` (fallback: meta.defaultHost). */
export const resolveSystemHostLabel = (channel: IChannelApiDto): string => {
  if (channel.host?.trim()) return channel.host.trim();
  const defaultHost = channel.meta?.defaultHost;
  if (typeof defaultHost === 'string' && defaultHost.trim()) {
    return defaultHost.trim();
  }
  return '—';
};

/** Effective output host based on saved meta.hostType. */
export const resolveChannelHostLabel = (channel: IChannelApiDto): string => {
  if (channel.meta?.hostType === 'personal') {
    const hostValue = channel.meta?.hostValue;
    if (typeof hostValue === 'string' && hostValue.trim()) {
      return hostValue.trim();
    }
  }

  return resolveSystemHostLabel(channel);
};

export const channelsApi = createApi({
  reducerPath: 'channelsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Channels'],
  endpoints: build => ({
    getChannelsByStockOwner: build.query<IChannelApiDto[], number>({
      query: stockOwnerId => ({
        url: `/system/channels/stock-owner/${stockOwnerId}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: unknown[] }) =>
        (response.data ?? []).map(item => deserializeChannel(item as Record<string, unknown>)),
      providesTags: (_result, _error, stockOwnerId) => [
        { type: 'Channels', id: `LIST-${stockOwnerId}` },
      ],
    }),

    updateChannelHostMeta: build.mutation<IChannelApiDto, IUpdateChannelHostMetaDto>({
      query: ({ id, hostType, hostValue }) => ({
        url: `/system/channels/${id}/host`,
        method: 'PATCH',
        body: { hostType, hostValue },
      }),
      transformResponse: (response: { data: unknown }) =>
        deserializeChannel(response.data as Record<string, unknown>),
      invalidatesTags: (result) =>
        result != null
          ? [{ type: 'Channels', id: `LIST-${result.stockOwnerId}` }]
          : ['Channels'],
    }),
  }),
});

export const {
  useGetChannelsByStockOwnerQuery,
  useUpdateChannelHostMetaMutation,
} = channelsApi;
